import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, from, map, shareReplay } from 'rxjs';

import { ToastService } from '@plugins/client/toast/toast.service';
import { TranslateService } from '@plugins/client/translate/services';
import { NotificationContent } from '@plugins/middleware-api';
import { SDK } from '@plugins/middleware-sdk';
import {
  PlatformPermission,
  ShareExternalContent,
} from '@plugins/shared/types';

@Injectable({ providedIn: 'root' })
export class ShareService {
  private readonly http = inject(HttpClient);
  private readonly sdk = inject(SDK);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  private readonly notificationFeature$ = from(
    this.sdk.isFeatureEnabled('notification'),
  ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  private readonly userpickerFeature$ = from(
    this.sdk.isFeatureEnabled('userpicker'),
  ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  private readonly pluginurlFeature$ = from(
    this.sdk.isFeatureEnabled('pluginurl'),
  ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  private readonly clipboardPermissions$ = from(
    this.sdk.permissionsAvailable([
      PlatformPermission.clipboardRead,
      PlatformPermission.clipboardWrite,
    ]),
  ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  private readonly internalAvailable$ = combineLatest([
    this.notificationFeature$,
    this.userpickerFeature$,
  ]).pipe(
    map(([notifications, picker]) => notifications && picker),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly internalAvailable = toSignal(this.internalAvailable$);

  private readonly externalAvailable$ = combineLatest([
    this.pluginurlFeature$,
    from(this.sdk.canShareExternally()),
  ]).pipe(
    map(([pluginurl, canShareExternally]) => pluginurl && canShareExternally),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly externalAvailable = toSignal(this.externalAvailable$);

  private readonly copyLinkAvailable$ = combineLatest([
    this.pluginurlFeature$,
    this.clipboardPermissions$,
  ]).pipe(map(([pluginurl, clipboard]) => pluginurl && clipboard));

  readonly copyLinkAvailable = toSignal(this.copyLinkAvailable$);

  private readonly _externalEnabled = signal(false);
  readonly externalEnabled = this._externalEnabled.asReadonly();

  readonly shareAvailable = computed(
    () =>
      (this.externalAvailable() && this.externalEnabled()) ||
      this.internalAvailable() ||
      this.copyLinkAvailable(),
  );

  shareInternally(
    content: NotificationContent,
    recipients: string[],
    link: string,
  ) {
    return this.http.post('/api/share', {
      content,
      recipients,
      link,
    });
  }

  async shareExternally(content: ShareExternalContent) {
    let isAbsoluteUrl: boolean;
    try {
      isAbsoluteUrl = !!new URL(content.url);
    } catch {
      isAbsoluteUrl = false;
    }
    if (!isAbsoluteUrl) {
      const pluginUrl = await this.sdk.getUserPluginUrl();
      content = { ...content, url: pluginUrl + content.url };
    }
    if (this.externalAvailable()) {
      return this.sdk.shareExternally(content);
    }
    return Promise.resolve(false);
  }

  async copyLink() {
    try {
      const url = await this.sdk.getUserPluginUrl();
      await navigator.clipboard.writeText(url);
      const message = this.translateService.instant('share.linkToClipboard');
      if (message) {
        this.toastService.show({ message, closeTimeout: 5000 });
      }
    } catch {
      console.warn('Failed writing link to clipboard.');
    }
  }

  setExternalEnabled(enabled: boolean) {
    this._externalEnabled.set(enabled);
  }
}
